import { DeviceConst } from '../../../consts';

/**
 * Constants for the Device Registry sub-module.
 * Provides route paths, translation keys, and API endpoints for physical device management.
 */
export abstract class DeviceRegistryConst {

    /**
     * The display title for the Device Registry module.
     */
    public static readonly TITLE = DeviceConst.CHILDREN.REGISTRY.TITLE;

    /**
     * The i18n translation key for the Device Registry sub-module.
     */
    public static readonly TRANSLATE_KEY = DeviceConst.CHILDREN.REGISTRY.TRANSLATE_KEY();

    /**
     * The breadcrumb translation key for the Device Registry module.
     */
    public static readonly BREADCRUMB = DeviceConst.CHILDREN.REGISTRY.BREADCRUMB();

    /**
     * The URL route segment for the Device Registry module.
     */
    public static readonly ROUTE_PAGE = DeviceConst.CHILDREN.REGISTRY.ROUTE_PAGE;

    /**
     * The full absolute route link for the Device Registry module.
     */
    public static readonly ROUTE_LINK = DeviceConst.CHILDREN.REGISTRY.ROUTE_LINK();

    /**
     * Root API endpoint prefix for Device requests.
     */
    public static readonly _API_PREFIX_ENDPOINT = DeviceConst.API_PREFIX_ENDPOINT;

    // ── CRUD Endpoints ──────────────────────────────────────────────────────

    /**
     * GET all devices.
     */
    public static readonly LIST_ENDPOINT = 'devices';

    /**
     * GET a single device by ID.
     */
    public static readonly GET_BY_ID_ENDPOINT = (id: string): string =>
        `${DeviceRegistryConst._API_PREFIX_ENDPOINT}/${id}`;

    /**
     * POST register / create new device(s).
     */
    public static readonly CREATE_ENDPOINT = `${DeviceRegistryConst._API_PREFIX_ENDPOINT}/register`;

    /**
     * PUT update a device.
     */
    public static readonly UPDATE_ENDPOINT = `${DeviceRegistryConst._API_PREFIX_ENDPOINT}/update`;

    /**
     * DELETE a device by ID.
     */
    public static readonly DELETE_ENDPOINT = (id: string): string =>
        `${DeviceRegistryConst._API_PREFIX_ENDPOINT}/delete/${id}`;

    // ── Command Endpoint ─────────────────────────────────────────────────────

    /**
     * POST send a raw command to a device.
     */
    public static readonly SEND_COMMAND_ENDPOINT = `${DeviceRegistryConst._API_PREFIX_ENDPOINT}/rpc`;

    /**
     * POST execute a safe RPC command for a control board widget.
     */
    public static readonly RPC_SAFE_ENDPOINT = `${DeviceRegistryConst._API_PREFIX_ENDPOINT}/rpc_safe`;

    // ── Credential Generation Endpoints ──────────────────────────────────────

    /**
     * GET generate a device username (credential ID).
     */
    public static readonly GEN_CRED_ID_ENDPOINT = `${DeviceRegistryConst._API_PREFIX_ENDPOINT}/gen_cred_id`;

    /**
     * GET generate a device password (credential value).
     */
    public static readonly GEN_CRED_VAL_ENDPOINT = `${DeviceRegistryConst._API_PREFIX_ENDPOINT}/gen_cred_val`;

    // ── Lookup Endpoints ─────────────────────────────────────────────────────

    /**
     * GET list of available gateways for device assignment.
     */
    public static readonly GATEWAY_LIST_ENDPOINT = `${DeviceRegistryConst._API_PREFIX_ENDPOINT}/gateway/list`;

    /**
     * GET list of buildings (units first level) — consumed by core LookupService.
     */
    public static readonly BUILDING_LIST_ENDPOINT = 'units/building/list';

    /**
     * GET units (children) belonging to a specific building.
     */
    public static readonly UNITS_BY_BUILDING_ENDPOINT = (buildingId: string): string =>
        `units/list/${buildingId}`;

    /**
     * GET list of SADIN clients — consumed by core LookupService.
     */
    public static readonly SADIN_CLIENTS_ENDPOINT = `${DeviceRegistryConst._API_PREFIX_ENDPOINT}/sadin_clients`;

    // ── MQTT Topics ──────────────────────────────────────────────────────────

    /**
     * MQTT topic for real-time device online/offline status updates.
     */
    public static readonly MQTT_DEVICE_STATUS_TOPIC = 'req/device/status';

    // ── Child Pages ──────────────────────────────────────────────────────────

    /**
     * Child page configurations for the Device Registry module.
     */
    public static readonly CHILDREN = {

        /**
         * The main list page — displays the device data table.
         */
        LIST: {
            NAME: 'LIST',
            TITLE: 'Devices List',
            ROUTE_PAGE: 'list',
            TRANSLATE_KEY: () => {
                return DeviceRegistryConst.TRANSLATE_KEY;
            },
            BREADCRUMB: () => {
                return [DeviceRegistryConst.TRANSLATE_KEY, 'LIST', 'TITLE'].join('.');
            },
            ROUTE_LINK: function () {
                return [DeviceRegistryConst.ROUTE_LINK, this.ROUTE_PAGE].join('/');
            },
        },

        /**
         * Telemetry placeholder — navigates to live telemetry keys for a device.
         * Page implementation is deferred.
         */
        TELEMETRY: {
            NAME: 'TELEMETRY',
            TITLE: 'Telemetry',
            ROUTE_PAGE: 'telemetry',
            TRANSLATE_KEY: () => {
                return [DeviceRegistryConst.TRANSLATE_KEY, 'TELEMETRY'].join('.');
            },
            BREADCRUMB: () => {
                return [DeviceRegistryConst.CHILDREN.TELEMETRY.TRANSLATE_KEY(), 'TITLE'].join('.');
            },
            ROUTE_LINK: (deviceId: string) => {
                return [DeviceRegistryConst.ROUTE_LINK, 'telemetry', deviceId].join('/');
            },
        },

        /**
         * Config placeholder — navigates to device properties and settings.
         * Page implementation is deferred.
         */
        CONFIG: {
            NAME: 'CONFIG',
            TITLE: 'Config',
            ROUTE_PAGE: 'config',
            TRANSLATE_KEY: () => {
                return [DeviceRegistryConst.TRANSLATE_KEY, 'CONFIG'].join('.');
            },
            BREADCRUMB: () => {
                return [DeviceRegistryConst.CHILDREN.CONFIG.TRANSLATE_KEY(), 'TITLE'].join('.');
            },
            ROUTE_LINK: (deviceId: string) => {
                return [DeviceRegistryConst.ROUTE_LINK, 'config', deviceId].join('/');
            },
        },

    };

}
